/**
 * 後台 — 品牌活動管理
 *
 * 管 LINE「品牌活動」carousel 卡片 + LIFF 完整紀錄頁,一筆活動兩邊一起生:
 *   卡片(必填):主圖 4:3 / 標題 / 日期 / 幾行預覽文字 → LINE 藝境・私宴 > 品牌活動
 *   完整頁(勾「啟用完整紀錄頁」才出現):引言 / 內文段落 / 重點區塊 / 花絮相簿 /
 *   對應商品 → 卡片多「閱讀完整紀錄」按鈕跳 /brand/events/{slug}
 * 下架 = 卡片不進 LINE carousel;完整頁舊連結仍可開。
 */
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminListBrandEvents,
  adminUpsertBrandEvent,
  adminDeleteBrandEvent,
  adminToggleBrandEvent,
  adminUploadBrandEventImage,
  adminSeedBrandEvents,
  type BrandEventDoc,
} from "../../api/admin";
import AdminHeader from "../../components/AdminHeader";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";

type SectionItem = { icon: string; title: string; body: string };

type FormState = {
  slug: string; // 編輯才有(doc id,不可改);新增為空
  newSlug: string; // 新增時選填(網址代號,小寫英數-)
  title_zh: string;
  title_en: string;
  date: string;
  venue: string;
  sortOrder: string;
  active: boolean;
  cardImage: string;
  cardLinesText: string; // 一行一句
  hasDetail: boolean;
  eyebrow: string;
  heroImage: string;
  heroAspect: "4/3" | "3/4";
  lead: string;
  intro: string[];
  sections: SectionItem[];
  closing: string[];
  tagsText: string; // 逗號分隔
  gallery: string[];
  rpName: string;
  rpSpec: string;
  rpNote: string;
  rpProductId: string;
};

const EMPTY_FORM: FormState = {
  slug: "", newSlug: "",
  title_zh: "", title_en: "", date: "", venue: "",
  sortOrder: "0", active: true,
  cardImage: "", cardLinesText: "",
  hasDetail: false,
  eyebrow: "", heroImage: "", heroAspect: "4/3", lead: "",
  intro: [], sections: [], closing: [],
  tagsText: "", gallery: [],
  rpName: "", rpSpec: "", rpNote: "", rpProductId: "",
};

function docToForm(d: BrandEventDoc): FormState {
  return {
    slug: d.slug,
    newSlug: "",
    title_zh: d.title_zh || "",
    title_en: d.title_en || "",
    date: d.date || "",
    venue: d.venue || "",
    sortOrder: String(d.sort_order ?? 0),
    active: !!d.active,
    cardImage: d.card_image || "",
    cardLinesText: (d.card_lines || []).join("\n"),
    hasDetail: !!d.has_detail,
    eyebrow: d.eyebrow || "",
    heroImage: d.hero_image || "",
    heroAspect: d.hero_aspect === "3/4" ? "3/4" : "4/3",
    lead: d.lead || "",
    intro: d.intro || [],
    sections: (d.sections || []).map((s) => ({ icon: s.icon || "", title: s.title || "", body: s.body || "" })),
    closing: d.closing || [],
    tagsText: (d.tags || []).join(", "),
    gallery: d.gallery || [],
    rpName: d.related_product?.name || "",
    rpSpec: d.related_product?.spec || "",
    rpNote: d.related_product?.note || "",
    rpProductId: d.related_product?.product_id || "",
  };
}

export default function BrandEventManage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [rows, setRows] = useState<BrandEventDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [uploadingKey, setUploadingKey] = useState(""); // "card" / "hero" / "gallery"

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function refresh() {
    setLoading(true);
    try {
      setRows(await adminListBrandEvents());
    } catch {
      showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  function startNew() {
    setForm(EMPTY_FORM);
    setEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function startEdit(d: BrandEventDoc) {
    setForm(docToForm(d));
    setEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function cancel() {
    setForm(EMPTY_FORM);
    setEditing(false);
  }
  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // 選好檔案 → 立刻上傳到圖庫 → 回 url
  async function uploadImage(file: File | undefined, key: string): Promise<string | null> {
    if (!file) return null;
    setUploadingKey(key);
    try {
      const r = await adminUploadBrandEventImage(file);
      if (r.status === "ok" && r.url) return r.url;
      showToast(`上傳失敗:${r.status}`, "error");
      return null;
    } catch {
      showToast("上傳失敗(檔案太大或格式不符)", "error");
      return null;
    } finally {
      setUploadingKey("");
    }
  }
  async function pickCardImage(file?: File) {
    const url = await uploadImage(file, "card");
    if (url) set("cardImage", url);
  }
  async function pickHero(file?: File) {
    const url = await uploadImage(file, "hero");
    if (url) set("heroImage", url);
  }
  async function addGalleryImages(files: FileList | null) {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const url = await uploadImage(file, "gallery");
      if (url) setForm((f) => ({ ...f, gallery: [...f.gallery, url] }));
    }
  }
  function removeGallery(i: number) {
    setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));
  }

  // 段落 list editor(intro / closing 共用)
  function listAdd(k: "intro" | "closing") {
    setForm((f) => ({ ...f, [k]: [...f[k], ""] }));
  }
  function listUpdate(k: "intro" | "closing", i: number, v: string) {
    setForm((f) => ({ ...f, [k]: f[k].map((p, idx) => (idx === i ? v : p)) }));
  }
  function listRemove(k: "intro" | "closing", i: number) {
    setForm((f) => ({ ...f, [k]: f[k].filter((_, idx) => idx !== i) }));
  }
  // 重點區塊
  function sectionAdd() {
    setForm((f) => ({ ...f, sections: [...f.sections, { icon: "", title: "", body: "" }] }));
  }
  function sectionUpdate(i: number, field: keyof SectionItem, v: string) {
    setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)) }));
  }
  function sectionRemove(i: number) {
    setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    if (!form.title_zh.trim()) {
      showToast("請填活動標題(中)", "error");
      return;
    }
    if (!form.cardImage.trim()) {
      showToast("請上傳卡片主圖(LINE 卡片一定要有圖)", "error");
      return;
    }
    const cardLines = form.cardLinesText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (cardLines.length === 0) {
      showToast("請填卡片預覽文字(至少一行)", "error");
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminUpsertBrandEvent({
        slug: form.slug || undefined,
        new_slug: form.newSlug.trim() || undefined,
        title_zh: form.title_zh.trim(),
        title_en: form.title_en.trim(),
        date: form.date.trim(),
        venue: form.venue.trim(),
        sort_order: parseInt(form.sortOrder, 10) || 0,
        active: form.active,
        card_image: form.cardImage.trim(),
        card_lines: cardLines,
        has_detail: form.hasDetail,
        eyebrow: form.eyebrow.trim(),
        hero_image: form.heroImage.trim(),
        hero_aspect: form.heroAspect,
        lead: form.lead.trim(),
        intro: form.intro.map((p) => p.trim()).filter(Boolean),
        sections: form.sections
          .map((s) => ({ icon: s.icon.trim(), title: s.title.trim(), body: s.body.trim() }))
          .filter((s) => s.title || s.body),
        closing: form.closing.map((p) => p.trim()).filter(Boolean),
        tags: form.tagsText.split(/[,,、]/).map((t) => t.trim()).filter(Boolean),
        gallery: form.gallery.filter((u) => u.trim()),
        related_product: {
          name: form.rpName.trim(),
          spec: form.rpSpec.trim(),
          note: form.rpNote.trim(),
          product_id: form.rpProductId.trim(),
        },
      });
      if (r.status === "ok") {
        showToast(r.created ? "已新增活動" : "已更新", "success");
        cancel();
        await refresh();
      } else {
        showToast(r.hint || `儲存未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggle(d: BrandEventDoc) {
    try {
      const r = await adminToggleBrandEvent(d.slug, !d.active);
      if (r.status === "ok") {
        showToast(!d.active ? "已上架" : "已下架", "success");
        await refresh();
      } else {
        showToast(`未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    }
  }

  async function remove(d: BrandEventDoc) {
    if (!confirm(`確定刪除活動「${d.title_zh}」?LINE 卡片與完整頁會一起消失。`)) return;
    try {
      const r = await adminDeleteBrandEvent(d.slug);
      if (r.status === "ok") {
        showToast("已刪除", "success");
        await refresh();
      } else {
        showToast(`刪除未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    }
  }

  async function seed() {
    setSeeding(true);
    try {
      const r = await adminSeedBrandEvents();
      if (r.status === "ok") {
        showToast(`已帶入 ${r.created} 場(略過已存在 ${r.skipped})`, "success");
        await refresh();
      } else {
        showToast(`帶入未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="brand-events" />
      <div className="p-6 max-w-3xl">
        {!editing ? (
          <>
            <div className="flex items-center justify-between mb-5">
              <h1 className="text-xl font-bold text-ink">品牌活動</h1>
              <button onClick={startNew} className="btn-primary">＋ 新增活動</button>
            </div>
            <p className="text-xs text-muted mb-4">
              上架的活動會出現在 LINE「藝境・私宴 → 品牌活動」卡片(依排序小到大);
              勾了完整紀錄頁的,卡片會多「閱讀完整紀錄」按鈕。
            </p>
            <div className="card">
              {loading ? (
                <div className="py-10 text-center text-muted">載入中…</div>
              ) : rows.length === 0 ? (
                <div className="py-10 text-center text-muted space-y-4">
                  <div>尚無活動資料。</div>
                  <button onClick={seed} disabled={seeding} className="btn-secondary text-sm px-4">
                    {seeding ? "帶入中…" : "一鍵帶入現有 6 場活動"}
                  </button>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted text-xs border-b border-line">
                      <th className="py-2">卡片</th>
                      <th className="py-2">活動</th>
                      <th className="py-2">完整頁</th>
                      <th className="py-2">狀態</th>
                      <th className="py-2">排序</th>
                      <th className="py-2 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.slug} className="border-b border-line/50">
                        <td className="py-2 pr-2">
                          {d.card_image ? (
                            <img src={d.card_image} alt="" className="w-16 h-12 object-cover rounded border border-line" />
                          ) : (
                            <div className="w-16 h-12 bg-cream rounded border border-line" />
                          )}
                        </td>
                        <td className="py-3 pr-2">
                          <div className="font-medium text-ink">{d.title_zh}</div>
                          <div className="text-xs text-muted">{d.date}</div>
                        </td>
                        <td className="py-3 text-xs text-muted">{d.has_detail ? "有" : "—"}</td>
                        <td className="py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${d.active ? "bg-primary/10 text-primary" : "bg-line text-muted"}`}>
                            {d.active ? "上架" : "下架"}
                          </span>
                        </td>
                        <td className="py-3 text-xs text-muted">{d.sort_order}</td>
                        <td className="py-3 text-right space-x-3 whitespace-nowrap">
                          <button onClick={() => startEdit(d)} className="text-primary text-xs underline">編輯</button>
                          <button onClick={() => toggle(d)} className="text-ink text-xs underline">
                            {d.active ? "下架" : "上架"}
                          </button>
                          <button onClick={() => remove(d)} className="text-coral text-xs underline">刪除</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <button onClick={cancel} className="text-primary text-xl">‹</button>
              <h1 className="text-xl font-bold text-ink">{form.slug ? `編輯:${form.title_zh || form.slug}` : "新增活動"}</h1>
            </div>

            <Section title="基本資料">
              <Field label="活動標題(中)*" full>
                <input className="input" value={form.title_zh} onChange={(e) => set("title_zh", e.target.value)} placeholder="仲夏養妍品鑒沙龍" />
              </Field>
              <Field label="活動標題(英)">
                <input className="input" value={form.title_en} onChange={(e) => set("title_en", e.target.value)} placeholder="Midsummer Tasting Salon" />
              </Field>
              <Field label="日期(顯示用文字)">
                <input className="input" value={form.date} onChange={(e) => set("date", e.target.value)} placeholder="2026.5.27" />
              </Field>
              <Field label="場地">
                <input className="input" value={form.venue} onChange={(e) => set("venue", e.target.value)} placeholder="遠企飯店" />
              </Field>
              <Field label="排序(小的排前面)">
                <input className="input" type="number" value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} />
              </Field>
              {!form.slug ? (
                <Field label="網址代號(選填,小寫英數與 -,留空自動產)" full>
                  <input className="input font-mono" value={form.newSlug} onChange={(e) => set("newSlug", e.target.value)} placeholder="midsummer-2605" />
                </Field>
              ) : (
                <Field label="網址代號(建立後不可改)" full>
                  <input className="input font-mono bg-cream" value={form.slug} readOnly disabled />
                </Field>
              )}
              <Field label="" full>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} />
                  上架(出現在 LINE 品牌活動卡片)
                </label>
              </Field>
            </Section>

            {/* LINE 卡片 */}
            <div className="card space-y-3 mb-4">
              <div className="font-bold text-ink text-sm">LINE 卡片</div>
              <div>
                <label className="label">卡片主圖 *(建議橫式,會裁成 4:3)</label>
                <div className="flex items-center gap-3">
                  {form.cardImage ? (
                    <img src={form.cardImage} alt="" className="w-28 h-21 object-cover rounded border border-line shrink-0" style={{ height: "5.25rem" }} />
                  ) : (
                    <div className="w-28 bg-cream rounded border border-line flex items-center justify-center text-muted text-xs shrink-0" style={{ height: "5.25rem" }}>無圖</div>
                  )}
                  <label className="btn-secondary text-sm px-4 py-1.5 cursor-pointer text-center">
                    {uploadingKey === "card" ? "上傳中…" : form.cardImage ? "更換主圖" : "上傳主圖"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={!!uploadingKey}
                      onChange={(e) => { pickCardImage(e.target.files?.[0]); e.target.value = ""; }}
                    />
                  </label>
                </div>
              </div>
              <div>
                <label className="label">卡片預覽文字 *(一行一句,1~3 行;第一行深色其餘淺色)</label>
                <textarea
                  className="input resize-none"
                  rows={3}
                  value={form.cardLinesText}
                  onChange={(e) => set("cardLinesText", e.target.value)}
                  placeholder={"於遠企飯店舉辦仲夏品鑒沙龍。\n三盅演繹 — 金箔燕窩、沾三吃、燕窩雞湯。"}
                />
              </div>
            </div>

            {/* 完整紀錄頁 */}
            <div className="card space-y-3 mb-4">
              <label className="flex items-center gap-2 font-bold text-ink text-sm cursor-pointer">
                <input type="checkbox" checked={form.hasDetail} onChange={(e) => set("hasDetail", e.target.checked)} />
                啟用完整紀錄頁(卡片多「閱讀完整紀錄」按鈕,開雜誌風長文頁)
              </label>

              {form.hasDetail && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="頁首小字(英文氛圍字)" full>
                      <input className="input" value={form.eyebrow} onChange={(e) => set("eyebrow", e.target.value)} placeholder="Midsummer Tasting Salon" />
                    </Field>
                    <Field label="引言(開頭橘邊條那句)" full>
                      <textarea className="input resize-none" rows={2} value={form.lead} onChange={(e) => set("lead", e.target.value)} placeholder="在盛夏最柔軟的午後…" />
                    </Field>
                  </div>

                  {/* 完整頁 hero */}
                  <div>
                    <label className="label">完整頁大圖(留空用卡片主圖)</label>
                    <div className="flex items-center gap-3">
                      {form.heroImage ? (
                        <img src={form.heroImage} alt="" className="w-28 h-21 object-cover rounded border border-line shrink-0" style={{ height: "5.25rem" }} />
                      ) : (
                        <div className="w-28 bg-cream rounded border border-line flex items-center justify-center text-muted text-xs shrink-0" style={{ height: "5.25rem" }}>用卡片圖</div>
                      )}
                      <div className="flex flex-col gap-2">
                        <label className="btn-secondary text-sm px-4 py-1.5 cursor-pointer text-center">
                          {uploadingKey === "hero" ? "上傳中…" : form.heroImage ? "更換大圖" : "上傳大圖"}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={!!uploadingKey}
                            onChange={(e) => { pickHero(e.target.files?.[0]); e.target.value = ""; }}
                          />
                        </label>
                        {form.heroImage && (
                          <button onClick={() => set("heroImage", "")} className="text-coral text-xs underline">移除(改用卡片圖)</button>
                        )}
                      </div>
                      <select
                        className="input w-auto"
                        value={form.heroAspect}
                        onChange={(e) => set("heroAspect", e.target.value as "4/3" | "3/4")}
                      >
                        <option value="4/3">橫式 4:3</option>
                        <option value="3/4">直式 3:4</option>
                      </select>
                    </div>
                  </div>

                  {/* 開場段落 */}
                  <ParagraphList
                    title="開場段落"
                    items={form.intro}
                    onAdd={() => listAdd("intro")}
                    onUpdate={(i, v) => listUpdate("intro", i, v)}
                    onRemove={(i) => listRemove("intro", i)}
                  />

                  {/* 重點區塊 */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-muted">重點區塊(例:三盅,每塊標題 + 說明,會員頁標題前自動帶一顆小圓點)</div>
                    {form.sections.map((s, i) => (
                      <div key={i} className="border border-line rounded-lg p-3 space-y-2 bg-cream/40">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted">第 {i + 1} 塊</span>
                          <button onClick={() => sectionRemove(i)} className="text-coral text-xs underline">刪除</button>
                        </div>
                        <input className="input" value={s.title} onChange={(e) => sectionUpdate(i, "title", e.target.value)} placeholder="第一盅 · 金箔燕窩" />
                        <textarea className="input resize-none" rows={2} value={s.body} onChange={(e) => sectionUpdate(i, "body", e.target.value)} placeholder="以最純粹的姿態登場…" />
                      </div>
                    ))}
                    <button onClick={sectionAdd} className="btn-secondary text-sm w-full">＋ 新增區塊</button>
                  </div>

                  {/* 收尾段落 */}
                  <ParagraphList
                    title="收尾段落(品牌哲學)"
                    items={form.closing}
                    onAdd={() => listAdd("closing")}
                    onUpdate={(i, v) => listUpdate("closing", i, v)}
                    onRemove={(i) => listRemove("closing", i)}
                  />

                  {/* tags */}
                  <div>
                    <label className="label">標籤(逗號分隔,頁尾小圓框)</label>
                    <input className="input" value={form.tagsText} onChange={(e) => set("tagsText", e.target.value)} placeholder="純粹, 無添加, 安心, 生活" />
                  </div>

                  {/* 花絮相簿 */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-muted">現場花絮(可一次選多張)</div>
                    {form.gallery.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {form.gallery.map((url, i) => (
                          <div key={`${url}-${i}`} className="relative">
                            <img src={url} alt="" className="w-full aspect-square object-cover rounded border border-line" />
                            <button
                              onClick={() => removeGallery(i)}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-coral text-white text-xs leading-none"
                              title="移除"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="btn-secondary text-sm w-full block text-center cursor-pointer">
                      {uploadingKey === "gallery" ? "上傳中…" : "＋ 新增花絮照片"}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        disabled={!!uploadingKey}
                        onChange={(e) => { addGalleryImages(e.target.files); e.target.value = ""; }}
                      />
                    </label>
                  </div>

                  {/* 對應商品 */}
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="對應商品名(選填)">
                      <input className="input" value={form.rpName} onChange={(e) => set("rpName", e.target.value)} placeholder="霽雲・恆時即飲" />
                    </Field>
                    <Field label="規格">
                      <input className="input" value={form.rpSpec} onChange={(e) => set("rpSpec", e.target.value)} placeholder="60ml × 174 入" />
                    </Field>
                    <Field label="備註一行">
                      <input className="input" value={form.rpNote} onChange={(e) => set("rpNote", e.target.value)} placeholder="全年滋養配方" />
                    </Field>
                    <Field label="商品編號(填了可點跳商品頁)">
                      <input className="input font-mono" value={form.rpProductId} onChange={(e) => set("rpProductId", e.target.value)} placeholder="heng_shi_ji" />
                    </Field>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={cancel} className="btn-secondary flex-1" disabled={submitting}>取消</button>
              <button onClick={save} className="btn-primary flex-1" disabled={submitting || !!uploadingKey}>
                {submitting ? "儲存中…" : form.slug ? "儲存變更" : "新增"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card mb-4">
      <div className="font-bold text-ink text-sm mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children, full }: { label: string; children: ReactNode; full?: boolean }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      {label && <label className="label">{label}</label>}
      {children}
    </div>
  );
}

function ParagraphList({
  title,
  items,
  onAdd,
  onUpdate,
  onRemove,
}: {
  title: string;
  items: string[];
  onAdd: () => void;
  onUpdate: (i: number, v: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted">{title}</div>
      {items.map((p, i) => (
        <div key={i} className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted">第 {i + 1} 段</span>
            <button onClick={() => onRemove(i)} className="text-coral text-xs underline">刪除</button>
          </div>
          <textarea className="input resize-none" rows={2} value={p} onChange={(e) => onUpdate(i, e.target.value)} />
        </div>
      ))}
      <button onClick={onAdd} className="btn-secondary text-sm w-full">＋ 新增一段</button>
    </div>
  );
}
